import '../../atoms/tabs-navigation/TabsNavigation';
import '../faq-list/FAQList';
import '../show-more/ShowMore';
import type { DefaultComponentType } from '../../../../src/utils/types';
import type { TabNavigationProps } from '../../atoms/tabs-navigation/TabsNavigation.create';
import type { ShowMoreTitles } from '../show-more/ShowMore';
import './Tabs.pcss';

type FAQTabProps = {
    heading: string;
    content: string;
};

export type TabProps = TabNavigationProps & {
    content: string | FAQTabProps[];
    showMoreTitles?: ShowMoreTitles;
};

export type TabsProps = DefaultComponentType & {
    items: TabProps[];
};

export default class TabsComponent extends HTMLElement {
    readonly #tabs: NodeListOf<HTMLElement> = this.querySelectorAll('[role="tab"]');

    readonly #panels: NodeListOf<HTMLElement> = this.querySelectorAll('[role="tabpanel"]');

    public connectedCallback(): void {
        if (this.#tabs.length === 0 || this.#panels.length === 0) {
            throw new Error('Tabs component is configured incorrectly');
        }

        this.#tabs.forEach((tab) => tab.addEventListener('click', () => this.#handleToggle(tab)));
    }

    public disconnectedCallback(): void {
        this.#tabs.forEach((tab) =>
            tab.removeEventListener('click', () => this.#handleToggle(tab))
        );
    }

    #handleToggle = (clickedTab: HTMLElement): void => {
        const panelSelector = clickedTab.getAttribute('aria-controls');

        if (!panelSelector) {
            return;
        }

        this.#tabs.forEach((tab) => this.#setActiveTab(tab, panelSelector));
        this.#panels.forEach((panel) => this.#setActivePanel(panel, panelSelector));
    };

    #setActiveTab = (tab: HTMLElement, panelSelector: string): void => {
        const isActive = tab.getAttribute('aria-controls') === panelSelector;
        tab.classList.toggle('nh-tabs-navigation__anchor--active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
    };

    #setActivePanel = (panel: HTMLElement, panelSelector: string): void => {
        panel.hidden = panel.id !== panelSelector;
    };
}

if (!window.customElements.get('nh-tabs')) {
    window.customElements.define('nh-tabs', TabsComponent);
}
